import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import SignIn from './components/Auth/SignIn';
import VerifyCode from './components/Auth/VerifyCode';
import AdminDashboard from './components/Dashboard/AdminDashboard';
import RemoteControlPage from './components/Dashboard/RemoteControlPage';
import IglooMetricsPage from './components/Dashboard/IglooMetricsPage';
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "./firebase/firebaseConfig";

const VALID_TABS = ['accounts', 'devices', 'igloos', 'health', 'sessions', 'status'];

// Redirect target for '/', unknown paths, and invalid tab names. Preserves the
// query string so email deep links like /?openConversation=<uuid> survive.
function RedirectToAccounts() {
  const location = useLocation();
  return <Navigate to={{ pathname: '/accounts', search: location.search }} replace />;
}

// Validates the :tab param before rendering the dashboard. Lives outside
// AdminDashboard: an early-return <Navigate> in there would change its hook
// count between renders. Rendering the same un-keyed <AdminDashboard/> for
// every valid tab keeps it mounted across tab switches.
function TabRoute() {
  const { tab } = useParams();
  if (!VALID_TABS.includes(tab)) {
    return <RedirectToAccounts />;
  }
  return <AdminDashboard />;
}

function App() {
  const [user, setUser] = useState(null);
  const [authResolved, setAuthResolved] = useState(false);
  const [step, setStep] = useState('signin'); // 'signin', 'verify'
  const [email, setEmail] = useState('');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        setEmail(currentUser.email);
      }
      setAuthResolved(true);
    });
    return () => unsubscribe();
  }, []);

  const handleEmailSubmit = (email) => {
    setEmail(email);
    setStep('verify');
  };

  let content;
  if (!authResolved) {
    // Firebase is still rehydrating the persisted session; rendering SignIn
    // here would flash it on every deep-linked cold load.
    content = (
      <div className="flex min-h-screen items-center justify-center text-muted-foreground">
        Loading…
      </div>
    );
  } else if (!user) {
    // The pre-auth wizard renders in place of the routes so the requested URL
    // is preserved; once sign-in completes, the auth listener flips `user` and
    // the deep-linked route renders. onVerificationSuccess stays a no-op for
    // the same reason — the auth listener drives the transition.
    content = step === 'verify'
      ? <VerifyCode email={email} onVerificationSuccess={() => {}} />
      : <SignIn onEmailSubmit={handleEmailSubmit} />;
  } else {
    content = (
      <Routes>
        <Route path="/devices/:deviceId/remote" element={<RemoteControlPage />} />
        <Route path="/igloos/:iglooId/metrics" element={<IglooMetricsPage />} />
        <Route path="/:tab" element={<TabRoute />} />
        <Route path="*" element={<RedirectToAccounts />} />
      </Routes>
    );
  }

  return <BrowserRouter>{content}</BrowserRouter>;
}

export default App;
